import { capitalizeFirstLetter, useIntl } from '@capsicum/common';
import { forwardRef } from 'react';
import styled from 'styled-components';

import { type DateParts, type OrderMap, useDateInput } from '../../../../hooks/components/useDateInput';
import { BaseTextInput, type BaseTextInputProps } from '../text-input/BaseTextInput';

export type BaseSelectDateInputProps = BaseTextInputProps & {
	onChange: (value: string) => void;
	onComplete?: (code: string) => void;
	value: string;
};

/**
 *
 * @param {BaseSelectDateInputProps} props - The component's props
 * @param {boolean} disabled - if the input should be disabled
 * @param {function} onChange - on input's text change callback
 */
export const BaseSelectDateInput = forwardRef<Omit<HTMLInputElement, 'onChange'>, BaseSelectDateInputProps>(
	({ disabled, error, label: fieldTitle, onChange, value, ...props }, ref) => {
		const { dateParts, handleInputChange, sortedLabels } = useDateInput({ dateString: value, onChange });

		const { $t } = useIntl();
		const translatedLabels = {
			day: $t('global.day'),
			month: $t('global.month'),
			year: $t('global.year')
		};

		return (
			<div>
				<S.SectionTitle>{fieldTitle}</S.SectionTitle>
				<S.Container $error={Boolean(error)}>
					{sortedLabels?.map((label, index) => {
						const key = label as keyof OrderMap;
						const inputValue = dateParts[key as keyof DateParts];
						const placeholder = label === 'year' ? 'YYYY' : label === 'month' ? 'MM' : 'DD';
						const text = translatedLabels[label as keyof DateParts];
						const isError = index === 0 && error;

						return (
							<BaseTextInput
								{...props}
								{...(props.id ? { id: `${props.id}-${label}` } : {})}
								className="full-width"
								disabled={disabled}
								error={isError ? error : error ? { message: ' ', type: 'error' } : undefined}
								hideRemainingTextLength
								key={label}
								label={capitalizeFirstLetter(text)}
								maxLength={label === 'year' ? 4 : 2}
								placeholder={placeholder}
								ref={isError ? ref : undefined}
								testID={`${label}-field`}
								value={inputValue}
								onChange={e => handleInputChange(key, e.target.value)}
							/>
						);
					})}
				</S.Container>
			</div>
		);
	}
);

namespace S {
	export const Container = styled.div<{ $error?: boolean }>`
		display: flex;
		flex: 1;
		gap: ${({ theme }) => theme.spacings.xsmall};
		flex-direction: row;
		height: 100%;
		flex-grow: 1;
		border-radius: 8px;

		.full-width {
			flex: 1;
		}
	`;

	export const SectionTitle = styled.span`
		${({ theme }) => theme.fonts.text.text4};
		display: block;
		color: ${({ theme }) => theme.colors.text.main.subtle};
		margin-bottom: ${({ theme }) => theme.spacings.xxxsmall};
	`;

	export const SupportingInputInfo = styled.span<{ $disabled?: boolean; $hasError: boolean }>`
		${({ theme }) => theme.fonts.text.caption2};
		color: ${({ theme }) => theme.colors.borders.error.enabled};
	`;
}

BaseSelectDateInput.displayName = 'SelectDate';
